import {
  createContext,
  useContext,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

/**
 * Table parts for the pages that hand-roll a `<table>` rather than going
 * through `DataView` — usually because they need grouped rows, spanned cells or
 * an inline editor that a column definition can't express.
 *
 * The value here is the sticky behaviour, which is easy to get wrong:
 *
 *   - `position: sticky` needs a *bounded* scroll container. A plain
 *     `overflow-x-auto` wrapper looks like one (the browser promotes
 *     `overflow-y` to `auto`) but its height grows with its content, so it
 *     never scrolls and the header never sticks. `TableScroll` therefore takes
 *     an explicit `maxHeight`.
 *   - `border-collapse` drops the borders on sticky cells while they overlap
 *     other cells, so the table uses `border-separate` and each cell draws its
 *     own bottom border.
 *   - A sticky cell must be opaque. A translucent background lets the columns
 *     scrolling underneath show straight through it.
 */

interface TableCtx {
  sticky: boolean;
}
const TableContext = createContext<TableCtx>({ sticky: false });

/**
 * Bounded scroll container. `maxHeight` is what makes `TableHead` stick; leave
 * it off for short tables that should grow with the page instead.
 */
export function TableScroll({
  maxHeight,
  className,
  children,
}: {
  maxHeight?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <TableContext.Provider value={{ sticky: !!maxHeight }}>
      <div
        className={cn(maxHeight ? 'overflow-auto' : 'overflow-x-auto', className)}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
}

export function Table({ className, children }: { className?: string; children: ReactNode }) {
  const { sticky } = useContext(TableContext);
  return (
    <table
      className={cn(
        'w-full text-left text-sm',
        sticky ? 'border-separate border-spacing-0' : 'border-collapse',
        className,
      )}
    >
      {children}
    </table>
  );
}

export function TableHead({ className, children }: { className?: string; children: ReactNode }) {
  return <thead className={className}>{children}</thead>;
}

export function TableBody({ className, children }: { className?: string; children: ReactNode }) {
  const { sticky } = useContext(TableContext);
  return (
    <tbody className={cn(!sticky && 'divide-y divide-border', className)}>{children}</tbody>
  );
}

export function TableRow({
  className,
  interactive,
  children,
  ...rest
}: {
  className?: string;
  /** Adds hover + press affordance. Pair with an `onClick`. */
  interactive?: boolean;
  children: ReactNode;
} & React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        'group transition-colors duration-100 ease-out',
        interactive && 'press cursor-pointer hover:bg-muted/40 focus-ring',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

type SortDirection = 'asc' | 'desc' | false;

export function TableHeaderCell({
  className,
  /** Pin to the left edge during horizontal scroll. Use on the identifying column. */
  stickyLeft,
  /** Current sort state, so the cell can publish `aria-sort`. */
  sorted,
  onSort,
  children,
  ...rest
}: {
  className?: string;
  stickyLeft?: boolean;
  sorted?: SortDirection;
  onSort?: () => void;
  children: ReactNode;
} & ThHTMLAttributes<HTMLTableCellElement>) {
  const { sticky } = useContext(TableContext);
  return (
    <th
      scope="col"
      aria-sort={
        onSort ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined
      }
      className={cn(
        'whitespace-nowrap border-b border-border bg-muted px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground',
        sticky && 'sticky top-0 z-20',
        stickyLeft && 'sticky left-0 z-30',
        className,
      )}
      {...rest}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className="inline-flex items-center gap-1 rounded uppercase tracking-wider focus-ring"
        >
          {children}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export function TableCell({
  className,
  stickyLeft,
  children,
  ...rest
}: {
  className?: string;
  stickyLeft?: boolean;
  children?: ReactNode;
} & TdHTMLAttributes<HTMLTableCellElement>) {
  const { sticky } = useContext(TableContext);
  return (
    <td
      className={cn(
        'px-3 align-middle text-foreground',
        // Row height follows the viewer's density preference — see the
        // `--row-py` / `--row-font` tokens in globals.css.
        'py-[var(--row-py)] text-[length:var(--row-font)]',
        (sticky || stickyLeft) && 'border-b border-border',
        stickyLeft && 'sticky left-0 z-10 bg-surface group-hover:bg-muted',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
